import React, { useMemo, useState, useCallback } from 'react';
import { connect, useLocation } from 'umi';
import { Layout, Justify, Segment, Button, Checkbox } from 'tea-component';
import moment from 'moment';
import classnames from 'classnames';
import {
  DBO_POINT,
  useInitBuriedPointInfo,
} from '@op/operationLog/buriedPoint';
import summaryPoint from '@op/operationLog/pages/summary';
import '@/pages/opinion/assets/scss/pages/summary/index.scss';
import { PAGE_NAME } from '@op/constants/page';
import ContentBody from '@op/components/ContentBody';
import BubbleCommon, {
  getSummaryTitleTips,
  showChitchatTipsMap,
} from '@op/components/BubbleCommon';
import useLanguage from '@op/hooks/useLanguage';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { downloadIcon } from '@op/constants/chart';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { useClusterCategories } from './hooks/useClusterCategories';
import { useAvgSentiments } from './hooks/useAvgSentiments';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import HotEventDetail from './components/HotEventDetail';
import HotEventPanel from './components/HotEventPanel';
import CardCommon from '@op/components/CardCommon';
import { HotEventProvider } from './context';
import { TSortBy } from './components/HotEventPanel/types';
import './index.scss';

import IconPositive from '@/pages/opinion/assets/imgs/new-positive.svg';
import IconNegative from '@/pages/opinion/assets/imgs/new-negative.svg';
import IconNeutral from '@/pages/opinion/assets/imgs/new-neutral.svg';
import CommonBubble from '@home/components/CommonBubble';
import { hotEventClusterCategoriesTipsMap, hotEventNegativeTips, hotEventNeutralTips, hotEventPositiveTips } from '@op/components/BubbleCommon/copywritingKey/hotEvent';

const { Content } = Layout;

const FILTER_INSTANCE_ID = 'SummaryHotEvent';

function SummaryHotEvent({ gameInfo, wholeGameInfo, dispatch }: any) {
  useInitBuriedPointInfo({ buriedPoint: summaryPoint });
  const { formatMessage, isEn } = useLanguage();

  // ─── URL 参数 ───
  const location = useLocation();
  const pageUrlObj = (location as any)?.query || {};

  // userDefaultValue 从 URL 参数读取（URL 参数优先于默认值）
  // 注：筛选器为单日模式，SingleDateRange 仅使用 end_time
  const userDefaultValue = useMemo(() => {
    const today = moment().format('YYYY-MM-DD');
    const urlDate = pageUrlObj.end_time || pageUrlObj.start_time;
    return {
      start_time: urlDate || today,
      end_time: urlDate || today,
      date_type: pageUrlObj.date_type || 'daily',
    };
  }, [pageUrlObj.start_time, pageUrlObj.end_time, pageUrlObj.date_type]);

  const titleTipsMap = getSummaryTitleTips(gameInfo.edition_unified_id);

  // ─── 筛选器 ───
  const {
    filter,
    setFilter,
    loading: filterLoading,
    ...filterProps
  } = useFilterCommon({
    keys: ['SingleDateRange'],
    instanceId: FILTER_INSTANCE_ID,
  });

  // ─── 同步日期到 Redux → FilterCommon UI ───
  // 编程式修改日期时，必须 dispatch 到 Redux，FilterCommon 才能读取到新值并触发 confirm 流程
  const onFilterDateSync = useCallback(
    (dateRange: { start_time: string; end_time: string }) => {
      const startTime = moment(dateRange.start_time)
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss');
      const endTime = moment(dateRange.end_time)
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss');
      dispatch({
        type: 'opinionMarketingFilter/UPDATE_FILTER',
        payload: {
          start_time: startTime,
          end_time: endTime,
          date_type: 'daily',
        },
        instanceId: FILTER_INSTANCE_ID,
      });
    },
    [dispatch],
  );

  const [sentimentValue, setSentimentValue] = useState(
    pageUrlObj.sentiment || 'total',
  );

  // 渠道类型：在主页面统一定义，下发给所有子组件/Hook（后续可结合切换逻辑使用 setChannelType）
  const [channelType] = useState<string>('total');

  // ─── Cluster 分类筛选 ───
  const {
    clusterCategoriesValue,
    setClusterCategoriesValue,
    clusterCategoryOptions,
    reset,
  } = useClusterCategories(gameInfo, filter, sentimentValue);

  // ─── Avg Sentiment 情感分组（动态控制 Segment tab 显示）───
  const { avgSentiments, reset: resetAvgSentiments } = useAvgSentiments(
    gameInfo,
    filter,
  );



  // 拼接"全部"选项的完整 Segment options
  const clusterSegmentOptions = useMemo(() => {
    if (clusterCategoryOptions.length === 0) return [];
    return [
      { text: formatMessage({ id: 'All' }), value: 'all' },
      ...clusterCategoryOptions.map((item) => {

        const bubbleTips = hotEventClusterCategoriesTipsMap?.[item.value] || null;
        const bubble = isEn ? bubbleTips?.en || '' : bubbleTips?.zh || '';
        return {
          text: isEn ? item.textEn : item.text,
          value: item.value,
          bubble
        };
      }),
    ];
  }, [clusterCategoryOptions, formatMessage]);

  // 排序方式：提升到主页，供下载等场景使用
  const [sortBy, setSortBy] = useState<TSortBy>('total_comments');

  // 视图切换: panel 左右分栏视图(默认) |  detail 详情(Summary)视图
  const [viewType, setViewType] = useState<'panel' | 'summary'>('panel');

  // chitchat
  const [showChitchat, setShowChitchat] = useState(true);

  // Segment: Total | Positive | Negative | Neutral
  // Positive / Negative / Neutral 仅在 avg_sentiments 接口返回对应分组时显示
  const tabOptions = useMemo(() => {
    const list: Array<{ text: React.ReactNode; value: string }> = [
      { text: formatMessage({ id: 'Total-2' }), value: 'total' },
      {
        text: (
          <BubbleCommon
            showIcon={false}
            placement="top"
            tipsMap={hotEventPositiveTips}
          >
            <div className="dbo-inlineflex">
              <img src={IconPositive} alt="" className="dbo-sentiment-icon" />
              {formatMessage({ id: 'Positive' })}
            </div>
          </BubbleCommon>
        ),
        value: 'positive',
      },
      {
        text: (
          <BubbleCommon
            showIcon={false}
            placement="top"
            tipsMap={hotEventNegativeTips}
          >
            <div className="dbo-inlineflex">
              <img src={IconNegative} alt="" className="dbo-sentiment-icon" />
              {formatMessage({ id: 'Negative' })}
            </div>
          </BubbleCommon>
        ),
        value: 'negative',
      }
    ];

    if (avgSentiments.includes('neutral')) {
      list.push({
        text: (
          <BubbleCommon
            showIcon={false}
            placement="top"
            tipsMap={hotEventNeutralTips}
          >
            <div className="dbo-inlineflex">
              <img src={IconNeutral} alt="" className="dbo-sentiment-icon" />
              {formatMessage({ id: 'Neutral' })}
            </div>
          </BubbleCommon>
        ),
        value: 'neutral',
      });
    }

    return list;
  }, [formatMessage, avgSentiments]);

  // Download handler — 分页拉取 list_v2 全部数据导出 Excel
  const { download, downloading } = useDownloadParams();
  const handleDownload = useCallback(() => {
    download({
      key: 'HotEventDataListDownload',
      filter: {
        ...gameInfo,
        ...filter,
        sentiment_type: sentimentValue,
        sort_item: sortBy,
        sort_model: 'desc',
        channel_type: channelType,
        filter_keyword_response: true,
        should_filter_general_event: !showChitchat
      },
      fileName: 'HotEvent_List',
    });
  }, [filter, gameInfo, sortBy, sentimentValue, channelType, showChitchat, download]);

  const rightButtons = useMemo(
    () => [
      {
        key: 'panel',
        pointId: 'HC_list',
        icon: 'card',
        onClick: () => setViewType('panel'),
        tips: formatMessage({ id: 'Event List' }),
      },
      {
        key: 'summary',
        pointId: 'HC_summary',
        icon: 'ai-task',
        onClick: () => setViewType('summary'),
        tips: formatMessage({ id: 'Event Summary' }),
      },
      {
        key: 'download',
        pointId: 'HC_download',
        icon: downloadIcon.icon,
        onClick: handleDownload,
        tips: formatMessage({ id: 'Download' }),
      },
    ],
    [handleDownload, formatMessage],
  );

  return (
    <Content className="dbo-summary-hot-event">
      <ContentBody
        contentHeard={
          <Content.Header
            title={
              <div className="dbo-header-title">
                {formatMessage({ id: PAGE_NAME.HOT_EVENT })}
              </div>
            }
            subtitle={
              titleTipsMap && (
                <div className="dbo-header-subtitle ">
                  <BubbleCommon tipsMap={titleTipsMap}>
                    {formatMessage({ id: 'Data Update' })}
                  </BubbleCommon>
                </div>
              )
            }
          ></Content.Header>
        }
        pageName={PAGE_NAME.HOT_EVENT}
      >
        <Justify
          className="dbo-summary-select"
          left={
            <div className="dbo-summary-select-flex">
              <FilterCommon
                {...filterProps}
                userDefaultValue={userDefaultValue}
                loadingHeight={40}
                useOriDefaultValue
                realTimeMode={true}
                createShortId={false}
                confirm={(value: any) => {
                  DBO_POINT.btn('C_time', value);
                  setFilter(value);
                  reset(value);
                  resetAvgSentiments(value);
                }}
              />
              {filterLoading ? null : (
                <Segment
                  value={sentimentValue}
                  className="segment-light"
                  onChange={(value) => {
                    setSentimentValue(value);
                    DBO_POINT.btn('HC_totalSentimentTab', value);
                    reset(undefined, value);
                  }}
                  options={tabOptions}
                />
              )}
              {filterLoading || clusterSegmentOptions.length === 0 || viewType === 'summary' ? null : (
                <Segment
                  value={clusterCategoriesValue}
                  className="segment-light"
                  onChange={(value) => {
                    setClusterCategoriesValue(value);
                  }}
                  options={clusterSegmentOptions}
                />
              )}
            </div>
          }
          right={
            filterLoading ? null : (
              <div className="dbo-summary-select-flex dbo-summary-select-flex-right">
                <BubbleCommon tipsMap={showChitchatTipsMap} showIcon={false}>
                  <Checkbox
                    value={showChitchat}
                    onClick={() => {
                      setShowChitchat(!showChitchat);
                      DBO_POINT.btn('HC_showChitchat');
                    }}
                    className="dbo-summary-select-checkbox"
                  >
                    {formatMessage({ id: 'Include Chitchat' })}
                  </Checkbox>
                </BubbleCommon>
                {rightButtons.map(({ key, icon, onClick, pointId, tips }) => {
                  return (
                    <CommonBubble content={tips} key={key}>
                      <Button
                        key={key}
                        type="link"
                        loading={key === 'download' ? downloading : undefined}
                        className={classnames('dbo-icon-btn', {
                          'dbo-icon-btn-active': viewType === key,
                        })}
                        onClick={() => {
                          onClick();
                          DBO_POINT.btn(pointId);
                        }}
                      >
                        <SvgIcon type={icon} />
                      </Button>
                    </CommonBubble>
                  );
                })}
              </div>
            )
          }
        />
        <HotEventProvider
          value={{
            gameInfo,
            wholeGameInfo,
            filter: {
              ...filter,
              should_filter_general_event: !showChitchat
            },
            onFilterDateSync,
            sentimentValue,
            channelType,
            sortBy,
            onSortByChange: setSortBy,
            clusterCategoriesValue,
          }}
        >
          <CardCommon contentCard loading={filterLoading} loadingHeight={600}>
            {viewType === 'panel' ? <HotEventPanel /> : <HotEventDetail />}
          </CardCommon>
        </HotEventProvider>
      </ContentBody>
    </Content>
  );
}

export default connect((state: any) => {
  const { opinionCommon } = state;

  return {
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
  };
})(SummaryHotEvent);
